import { useEffect, useState } from "react";

import { PhoneNumber } from "@/Common/Api/Contacts/Dto/PhoneNumber";
import { PrintHelper } from "@/Common/Helpers/PrintHelper";
import { PhoneType } from "@/Common/Models/Contacts/Enums/PhoneType";
import { Site } from "@/Common/Models/Sites/ResponseModels/Site";
import UploadSiteLogoPopup from "./UploadSiteLogoPopup";

import styles from "@/Administration/Components/Sites/SiteView/HeaderSection.module.scss";

import LogoMyle from "@/Globals/_images/logo-myle.svg";

interface IHeaderSectionProps {
    site: Site;
}

function getFormattedPreferredPhoneNumber(phoneType: PhoneType, phones: PhoneNumber[]): string | null {
    if (!phones?.length)
        return null;

    const lookupId = cacheStore.getLookupByPhoneType(phoneType)?.LookupID;

    const preferredPhone = phones.filter(phone => phone.PhoneTypeLookupId === lookupId)
        .firstPreferredOrDefault(phone => phone.IsPreferred);

    return preferredPhone && PhoneNumber.stringFormattedPhone(preferredPhone);
}

function HeaderSection(props: IHeaderSectionProps): JSX.Element {
    const enableSitePortalInformation = cacheStore.ClinicSettings.EnableSitePortalInformation;

    const { site } = props;
    const [isPopupShown, setIsPopupShown] = useState(false);
    const [imageSrc, setImageSrc] = useState("");
    const preferredAddress = site.Adresses?.length > 0 ? (site.Adresses?.find(a => a.IsPreferred) || site.Adresses[0]) : null;
    const siteAddress = preferredAddress && `${preferredAddress.StreetAddress}, ${preferredAddress.City}, ${cacheStore.GetLocalizedValueByLookupId(preferredAddress.StateLookupId)}`;

    const sitePhone = getFormattedPreferredPhoneNumber(PhoneType.Phone, site?.Phones);
    const siteFax = getFormattedPreferredPhoneNumber(PhoneType.Fax, site?.Phones);

    const getAvailableAttachment = (): string | null => {
        const translatedAttachment = site.LogoAttachments?.find(item => item?.Locale === sessionStore.locale);
        const defaultAttachment = site.LogoAttachments?.find(item => IsNullOrUndefined(item.Locale));

        if (!defaultAttachment && !translatedAttachment) {
            return null;
        }

        return PrintHelper.GetAttachmentPreviewPath(translatedAttachment?.AttachmentId || defaultAttachment?.AttachmentId) + `&dummy=${sessionStore.newId()}`;
    };

    useEffect(
        () => setImageSrc(getAvailableAttachment()),
        [site.LogoAttachments]
    );

    return (
        <>
            <div className="site-banner">
                {enableSitePortalInformation && (
                    <div className={styles.clinicLogoWrapper}>
                        {imageSrc ?
                            <img src={imageSrc} className={styles.clinicLogo} alt="myle-logo" />
                            : <LogoMyle className={styles.clinicLogo} />
                        }
                        <a onClick={() => setIsPopupShown(true)}>{cacheStore.GetString("ClinicInfo_ChangeClinicLogoLabel")}</a>
                    </div>
                )}
                <div>
                    <h3 title={site.ClinicalName} className="banner-title">{limitTextLength(site.ClinicalName, 80)}</h3>
                    {enableSitePortalInformation && (
                        <div className={styles.clinicInfoRow}>
                            <label className="form-label text-right">{cacheStore.GetString("AddressLabel")}</label>
                            <strong>{siteAddress}</strong>
                        </div>
                    )}

                    {enableSitePortalInformation && (
                        <div className={styles.clinicInfoRow}>
                            <label className="form-label text-right">{cacheStore.GetString("PhoneLabel")}</label>
                            <strong>{sitePhone}</strong>
                        </div>
                    )}

                    {enableSitePortalInformation && (
                        <div className={styles.clinicInfoRow}>
                            <label className="form-label text-right">{cacheStore.GetString("FaxLabel")}</label>
                            <strong>{siteFax}</strong>
                        </div>
                    )}
                </div>
            </div>
            {enableSitePortalInformation && isPopupShown && (
                <UploadSiteLogoPopup
                    logoAttachments={site.LogoAttachments}
                    siteId={site.Id}
                    onClose={() => setIsPopupShown(false)}
                    onUpdate={() => setImageSrc(getAvailableAttachment())}
                />
            )}
        </>
    );
}

export default HeaderSection;
